import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import PauseIcon from '@mui/icons-material/Pause';
import RefreshIcon from '@mui/icons-material/Refresh';

import { colors } from '../../theme/tokens';

export interface LivePollIndicatorProps {
  /** Poll interval in ms. Indicator counts down from this. */
  intervalMs: number;
  /** Whether polling is currently paused. */
  paused: boolean;
  /** Toggle pause/resume. */
  onTogglePause: () => void;
  /** Optional manual-refresh button. */
  onManualRefresh?: () => void;
}

/**
 * WU12 — "Refreshing in Ns / paused" affordance.
 *
 * Extracted pattern from `PipelineMonitorReal.tsx` (auto-refresh toggle).
 * The countdown is purely cosmetic — the caller's `useQuery` does the
 * actual polling via `refetchInterval`. We restart the local timer
 * whenever `intervalMs` or `paused` flips so the displayed value stays
 * roughly in sync with the next refetch.
 */
const LivePollIndicator: React.FC<LivePollIndicatorProps> = ({
  intervalMs,
  paused,
  onTogglePause,
  onManualRefresh,
}) => {
  const seconds = Math.max(1, Math.round(intervalMs / 1000));
  const [countdown, setCountdown] = useState(seconds);

  useEffect(() => {
    if (paused) return undefined;
    setCountdown(seconds);
    const id = setInterval(() => {
      setCountdown((s) => (s <= 1 ? seconds : s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [paused, seconds]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        onClick={onTogglePause}
        role="button"
        tabIndex={0}
        aria-label={paused ? 'Resume live updates' : 'Pause live updates'}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onTogglePause();
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          px: 1.5,
          py: 0.75,
          borderRadius: '10px',
          border: `1px solid ${!paused ? colors.successBorder : colors.borderDefault}`,
          bgcolor: !paused ? colors.successBg : colors.bgWhite,
          transition: 'all 0.2s',
          userSelect: 'none',
          '&:hover': { borderColor: !paused ? colors.successIcon : colors.borderHover },
          '&:focus-visible': { outline: `2px solid ${colors.brand}`, outlineOffset: 2 },
        }}
      >
        {!paused ? (
          <AutorenewIcon
            sx={{
              fontSize: 16,
              color: colors.successIcon,
              animation: 'livePollSpin 2s linear infinite',
              '@keyframes livePollSpin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
          />
        ) : (
          <PauseIcon sx={{ fontSize: 16, color: colors.textTertiary }} />
        )}
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            fontFamily: 'Manrope, sans-serif',
            color: !paused ? colors.successText : colors.textSecondary,
          }}
        >
          {!paused ? `Refreshing in ${countdown}s` : 'Paused'}
        </Typography>
      </Box>
      {onManualRefresh && (
        <Tooltip title="Refresh now">
          <IconButton
            size="small"
            onClick={onManualRefresh}
            aria-label="Refresh now"
            sx={{ color: colors.textSecondary }}
          >
            <RefreshIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default LivePollIndicator;
